import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getWordpressRedirect } from "@/data/wordpressRedirects";

/**
 * Renders nothing by itself — a catch-all React component that intercepts
 * legacy sterilemate.com WordPress URLs and redirects them to the correct
 * new React SPA route.
 *
 * Use as the LAST route in the App.js Route stack so it only fires when
 * no real route matched:
 *
 *   <Route path="*" element={<WordPressRedirect />} />
 *
 * If the path matches a legacy WordPress URL, we:
 *   1. Emit a <meta http-equiv="refresh"> tag with URL=<target>. This is
 *      the closest thing to a true server-side 301 for a static SPA —
 *      Google recognizes 0-second meta refresh as a 301-equivalent for
 *      link-authority transfer.
 *   2. Client-side navigate to the target (replace, so the legacy URL
 *      is removed from browser history).
 *
 * If the path does NOT match a legacy WordPress URL, we render a simple
 * 404 message so the user isn't stuck on a blank screen.
 */
export default function WordPressRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  // ── Trailing-slash normalizer ─────────────────────────────────────
  // React Router v6 does not match `/contact/` against a `<Route path="/contact">`.
  // Google indexed a mix of trailing / non-trailing URLs when the site was on
  // WordPress, so a bare `/contact/` was landing on the 404 splash — a dead page.
  // Strip a trailing slash (except for "/" itself) and re-navigate BEFORE we try
  // the WordPress-legacy lookup. This globally fixes any indexed `/foo/` variant
  // of any real SPA route.
  const hasTrailingSlash =
    location.pathname.length > 1 && location.pathname.endsWith("/");
  const normalizedPath = hasTrailingSlash
    ? location.pathname.replace(/\/+$/, "")
    : location.pathname;

  const target = getWordpressRedirect(normalizedPath);

  useEffect(() => {
    if (hasTrailingSlash) {
      // Preserve query string + hash on the redirect so ?utm=... survives.
      navigate(normalizedPath + location.search + location.hash, { replace: true });
      return;
    }
    if (!target) return;
    // Inject a meta refresh tag (0-second) — treated by Google as a 301 signal
    const meta = document.createElement("meta");
    meta.httpEquiv = "refresh";
    meta.content = `0; url=${target}`;
    meta.dataset.testid = "wp-redirect-meta";
    document.head.appendChild(meta);

    // Also emit a canonical hint so Google consolidates authority to the new URL
    const canonical = document.createElement("link");
    canonical.rel = "canonical";
    canonical.href = `https://sterilemate.com${target}`;
    canonical.dataset.testid = "wp-redirect-canonical";
    document.head.appendChild(canonical);

    // Client-side navigate immediately (no full reload)
    navigate(target, { replace: true });

    return () => {
      meta.remove();
      canonical.remove();
    };
  }, [target, navigate, hasTrailingSlash, normalizedPath, location.search, location.hash]);

  // While the trailing-slash redirect is in-flight, show a lightweight splash
  // so the buyer never sees the 404 screen even for a frame.
  if (hasTrailingSlash) {
    return (
      <div
        data-testid="trailing-slash-redirect-splash"
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Arial, sans-serif",
          color: "#64748b",
          fontSize: 14,
        }}
      >
        Loading&hellip;
      </div>
    );
  }

  if (target) {
    return (
      <div
        data-testid="wp-redirect-splash"
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Arial, sans-serif",
          color: "#64748b",
          fontSize: 14,
        }}
      >
        Redirecting to {target}&hellip;
      </div>
    );
  }

  return (
    <div
      data-testid="wp-redirect-404"
      style={{
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "Arial, sans-serif",
        color: "#0f172a",
        padding: "40px 20px",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 72, fontWeight: 900, color: "#0f2141", marginBottom: 8 }}>404</div>
      <h1 style={{ fontSize: 24, margin: "0 0 12px" }}>Page not found</h1>
      <p style={{ fontSize: 14, color: "#64748b", maxWidth: 480, marginBottom: 24 }}>
        The page <code style={{ background: "#f1f5f9", padding: "2px 6px", borderRadius: 4 }}>{location.pathname}</code>
        {" "}does not exist on sterilemate.com.
      </p>
      <a
        href="/"
        data-testid="wp-redirect-home-cta"
        style={{
          padding: "12px 28px",
          background: "#fbbf24",
          color: "#0f2141",
          textDecoration: "none",
          borderRadius: 8,
          fontWeight: 700,
        }}
      >
        Return to Sterile Mate home &rarr;
      </a>
    </div>
  );
}
